import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { MessagesSquare, Trash2, Plus, MessageCircle, PanelLeftClose, PanelLeft, Search, FolderKanban, FolderPlus } from 'lucide-react';
import { chatService } from '../services/chatService';
import { useQuery } from '@tanstack/react-query';
import { SubjectWithThreads, Thread } from '../types/subjects';
import { projectService } from '../services/projectService';
import { Project } from '../types/project';
import { NewProjectModal, SubjectOption } from './projects/NewProjectModal';
import { PROJECTS_HUB_ID } from './projects/ProjectsHub';
import { buildSubjectOptionDisplayMap } from '../utils/subjectDisplay';

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSubjectSelect: (subjectId: string | null) => void;
  onThreadSelect: (threadId: string) => void;
  onProjectSelect: (projectId: string | null) => void;
  onProjectDelete?: (projectId: string) => void;
  selectedSubject: string | null;
  selectedThreadId: string | null;
  selectedProjectId: string | null;
  onThreadDelete: (threadId: string) => void;
  subjects: SubjectWithThreads[];
  deletedThreadIds: Set<string>;
  deletedProjectIds?: Set<string>;
  isProfileOpen: boolean;
}

// Helper to group threads by date
const groupThreadsByDate = (threads: Thread[]) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

  const groups: { label: string; threads: Thread[] }[] = [
    { label: 'Today', threads: [] },
    { label: 'Yesterday', threads: [] },
    { label: 'Previous 7 Days', threads: [] },
    { label: 'Older', threads: [] },
  ];

  threads.forEach(thread => {
    const threadDate = new Date(thread.updated_at || thread.created_at || Date.now());
    
    if (threadDate >= today) {
      groups[0].threads.push(thread);
    } else if (threadDate >= yesterday) {
      groups[1].threads.push(thread);
    } else if (threadDate >= lastWeek) {
      groups[2].threads.push(thread);
    } else {
      groups[3].threads.push(thread);
    }
  });

  return groups.filter(group => group.threads.length > 0);
};

const Sidebar: React.FC<SidebarProps> = ({ 
  isOpen, 
  setIsOpen, 
  onSubjectSelect,
  onThreadSelect,
  onProjectSelect,
  onProjectDelete,
  selectedSubject,
  selectedThreadId,
  selectedProjectId,
  onThreadDelete,
  subjects: subjectsFromProps,
  deletedThreadIds,
  deletedProjectIds = new Set(),
  isProfileOpen
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);
  const [projectsRefresh, setProjectsRefresh] = useState(0);

  const { data: subjectsData = [], isLoading, error } = useQuery({
    queryKey: ['subjects_with_threads'],
    queryFn: chatService.getUserSubjects,
  });

  // Build subscribed subjects list for project creation modal
  const subscribedSubjects = useMemo<SubjectOption[]>(() => {
    const rawOptions = subjectsData
      .filter((s) => s.subject_id && s.name && s.subject_id !== 'general')
      .map((s) => ({ subject_id: s.subject_id, name: s.name }));
    const displayMap = buildSubjectOptionDisplayMap(
      rawOptions.map((option) => ({ id: option.subject_id, name: option.name }))
    );
    return rawOptions.map((option) => ({
      ...option,
      name: displayMap[option.subject_id] || option.name,
    }));
  }, [subjectsData]);

  const selectedSubjectThreads = useMemo(() => {
    if (!selectedSubject) return [];
    const subject = subjectsData.find(s => s.subject_id === selectedSubject);
    const base = (subject?.threads || []).filter(thread => !deletedThreadIds.has(thread.thread_id));
    const q = searchQuery.trim().toLowerCase();
    if (!q) return base;
    return base.filter(t => (t.title || '').toLowerCase().includes(q));
  }, [selectedSubject, subjectsData, deletedThreadIds, searchQuery]);

  const groupedThreads = useMemo(() => {
    return groupThreadsByDate(selectedSubjectThreads);
  }, [selectedSubjectThreads]);

  // Projects state (async loading)
  const [projects, setProjects] = useState<Project[]>([]);
  
  const loadProjects = useCallback(async () => {
    try {
      const all = await projectService.list();
      setProjects(all);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setProjects([]);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects, projectsRefresh]);

  // Filter projects by search query and exclude deleted ones
  const filteredProjects = useMemo<Project[]>(() => {
    const q = searchQuery.trim().toLowerCase();
    const base = projects.filter(p => !deletedProjectIds.has(p.id));
    if (!q) return base;
    return base.filter((p: Project) => {
      const hay = `${p.title || ''} ${p.subject_id || ''} ${p.form || ''} ${p.grade_level || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [projects, searchQuery, deletedProjectIds]);

  const handleNewChat = () => {
    // Clear thread selection to start fresh
    onThreadSelect('');
    onProjectSelect(null);
  };

  const handleOpenProjectsHub = () => {
    onProjectSelect(PROJECTS_HUB_ID);
    onThreadSelect('');
  };

  return (
    <div 
      data-tour="sidebar"
      className={`
      bg-[var(--bg-secondary)] 
      border-r border-[var(--border-color)] 
      flex flex-col h-full
      transition-all duration-300 ease-in-out
      ${isOpen ? 'w-64' : 'w-0 md:w-16'}
      ${isProfileOpen ? 'blur-sm pointer-events-none' : ''}
    `}>
      {/* Expanded State */}
      {isOpen && (
        <div className="flex flex-col h-full">
          {/* Top actions */}
          <div className="p-3 border-b border-[var(--border-color)] space-y-3">
            <button
              onClick={handleNewChat}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[var(--border-color)] 
                        hover:bg-[var(--bg-tertiary)] transition-all duration-200 group"
            >
              <Plus className="h-4 w-4 text-[var(--text-secondary)] group-hover:text-[var(--text-primary)]" />
              <span className="text-sm font-medium text-[var(--text-primary)]">New chat</span>
            </button>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-tertiary)]" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search projects & chats"
                className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-[var(--surface-elevated)] border border-[var(--border-color)] text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[var(--accent-primary)]/40"
              />
            </div>
          </div>

          {/* Scroll area: Projects + Chats */}
          <div className="flex-1 overflow-y-auto px-2 py-3 space-y-5">
            {/* Projects */}
            <div>
              <div className="flex items-center justify-between px-3 mb-2">
                <button
                  onClick={handleOpenProjectsHub}
                  className="text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wide hover:text-[var(--text-secondary)] transition-colors"
                >
                  Projects
                </button>
                <button
                  onClick={() => setIsNewProjectOpen(true)}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
                  title="New project"
                >
                  <FolderPlus className="h-3.5 w-3.5" />
                  New
                </button>
              </div>

              {projects.length === 0 ? (
                <div className="px-3 py-3 rounded-lg border border-[var(--border-color)] bg-[var(--surface-elevated)] text-xs text-[var(--text-tertiary)]">
                  No projects yet. Create one to get step-by-step project coaching.
                </div>
              ) : filteredProjects.length === 0 ? (
                <div className="px-3 py-3 text-xs text-[var(--text-tertiary)]">
                  No matching projects
                </div>
              ) : (
                <div className="space-y-0.5">
                  {filteredProjects.slice(0, 20).map((p) => (
                    <div
                      key={p.id}
                      className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-150
                        ${selectedProjectId === p.id
                          ? 'bg-[var(--accent-primary)]/10 text-[var(--text-primary)]'
                          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]'
                        }`}
                      onClick={() => {
                        // Note: handleProjectSelect already clears thread selection, so no need to call onThreadSelect
                        onProjectSelect(p.id);
                      }}
                    >
                      <FolderKanban className="h-4 w-4 flex-shrink-0 opacity-60" />
                      <span className="flex-1 text-sm truncate">{p.title || 'Untitled Project'}</span>
                      {onProjectDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onProjectDelete(p.id);
                          }}
                          className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-[var(--bg-quaternary)] transition-all"
                          title="Delete project"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-[var(--text-red)]" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Chats */}
            <div>
              <p className="text-xs font-medium text-[var(--text-tertiary)] px-3 mb-2 uppercase tracking-wide">
                Your chats
              </p>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[var(--accent-primary)]"></div>
              </div>
            ) : error ? (
              <div className="px-3 py-2 text-sm text-[var(--text-red)]">
                Error loading threads
              </div>
            ) : !selectedSubject ? (
              <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                <MessageCircle className="h-8 w-8 text-[var(--text-tertiary)] mb-3" />
                <p className="text-sm text-[var(--text-secondary)]">
                  Select a subject to view your chat history
                </p>
              </div>
            ) : groupedThreads.length > 0 ? (
              <div className="space-y-4">
                {groupedThreads.map((group) => (
                  <div key={group.label}>
                    <p className="text-xs font-medium text-[var(--text-tertiary)] px-3 mb-2 uppercase tracking-wide">
                      {group.label}
                    </p>
                    <div className="space-y-0.5">
                      {group.threads.map((thread: Thread) => (
                        <div
                          key={thread.thread_id}
                          className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-150
                            ${selectedThreadId === thread.thread_id
                              ? 'bg-[var(--accent-primary)]/10 text-[var(--text-primary)]'
                              : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]'
                            }`}
                          onClick={() => onThreadSelect(thread.thread_id)}
                        >
                          <MessageCircle className="h-4 w-4 flex-shrink-0 opacity-60" />
                          <span className="flex-1 text-sm truncate">{thread.title}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onThreadDelete(thread.thread_id);
                            }}
                            className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-[var(--bg-quaternary)] transition-all"
                            title="Delete thread"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-[var(--text-red)]" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                <MessagesSquare className="h-8 w-8 text-[var(--text-tertiary)] mb-3" />
                <p className="text-sm text-[var(--text-secondary)]">
                  No conversations yet
                </p>
                <p className="text-xs text-[var(--text-tertiary)] mt-1">
                  Start chatting to see your history here
                </p>
              </div>
            )}
            </div>
          </div>

          {/* Collapse Button */}
          <div className="p-3 border-t border-[var(--border-color)]">
            <button
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)] transition-colors w-full"
            >
              <PanelLeftClose className="h-4 w-4" />
              <span className="text-sm">Close sidebar</span>
            </button>
          </div>
        </div>
      )}

      {/* Collapsed State */}
      {!isOpen && (
        <div className="hidden md:flex flex-col items-center py-3 gap-2">
          <button
            onClick={() => setIsOpen(true)}
            className="p-2.5 hover:bg-[var(--bg-tertiary)] rounded-lg text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            title="Open sidebar"
          >
            <PanelLeft className="h-5 w-5" />
          </button>
          <button
            onClick={handleNewChat}
            className="p-2.5 hover:bg-[var(--bg-tertiary)] rounded-lg text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            title="New chat"
          >
            <Plus className="h-5 w-5" />
          </button>
          <button
            onClick={handleOpenProjectsHub}
            className="p-2.5 hover:bg-[var(--bg-tertiary)] rounded-lg text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            title="Projects"
          >
            <FolderKanban className="h-5 w-5" />
          </button>
        </div>
      )}

      <NewProjectModal
        isOpen={isNewProjectOpen}
        onClose={() => setIsNewProjectOpen(false)}
        onCreate={async (input) => {
          try {
            const created = await projectService.create({
              student_id: '', // Will be filled from auth
              subject_id: input.subject_id,
              title: input.title,  // Optional - AI can help pick a title
              project_brief: input.goal,
            });
            setProjectsRefresh((k) => k + 1);
            onProjectSelect(created.id);
            onThreadSelect('');
          } catch (err) {
            console.error('Failed to create project:', err);
          }
        }}
        subscribedSubjects={subscribedSubjects}
      />
    </div>
  );
};

export default Sidebar;